import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Search } from 'components/SearchBar/components';
import { CellStatus, Restricted } from 'components';
import { CELL_STATUSES } from 'common/constants';
import { CELL_GROUPS_CREATE, CELL_GROUPS_VIEW } from 'common/Right';
import { findCellGroups, saveCellGroup } from 'actions';
import {
  Modal,
  Card,
  CardContent,
  CardActions,
  Button,
  CardHeader,
  Divider,
  Menu,
  MenuItem,
  TableHead,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Grid,
  Checkbox,
  TextField,
  Typography
} from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 850,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  content: {
    padding: 0
  },
  actions: {
    justifyContent: 'flex-end'
  },
  progress: {
    marginBottom: theme.spacing(2)
  },
  fileName: {
    marginBottom: theme.spacing(2)
  },
  image: {
    width: '100%'
  },
  details: {
    padding: theme.spacing(2)
  },
  comment: {
    flexGrow: 1,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    borderColor: 'transparent',
    borderStyle: 'solid',
    borderWidth: 1,
    position: 'relative',
    marginTop: theme.spacing(1)
  },
  search: {
    minWidth: 480,
    maxWidth: 480,
    flexBasis: 480
  },
  tableContainer: {
    minHeight: 431,
    maxHeight: 431,
    overflowY: 'auto'
  },
  searchContainer: {
    padding: theme.spacing(2)
  },
  selfSearch: {
    marginTop: 3
  },
  buttonCell: {
    paddingTop: 0,
    paddingBottom: 0,
    width: 100
  },
  buttonExtraSmall: {
    padding: '1px 8px',
    fontSize: '0.8125rem'
  },
  labelCell: {
    paddingTop: 0,
    paddingBottom: 0
  },
  saveCellGroupButton: {
    marginLeft: theme.spacing(1)
  },
  saveCellGroupModalCard: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 380,
    maxWidth: '100%',
    backgroundColor: theme.palette.background.paper,
    zIndex: 1500
  },
  saveCellGroupModalActions: {
    justifyContent: 'flex-end'
  },
  saveCellGroupField: {
    width: '100%'
  }
}));

const SelectCells = props => {
  const {
    open,
    onClose,
    cells,
    selectedCells,
    onSelectCell,
    cellStatus,
    onChageCellStatus,
    hideCellGroupActions,
    className,
    ...rest
  } = props;

  const classes = useStyles();
  const dispatch = useDispatch();

  const loading = false;
  const [searchName, setSearchName] = useState('');
  const [cellGroupMenuAnchor, setCellGroupMenuAnchor] = useState(null);
  const [saveCellGroupOpen, setSaveCellGroupOpen] = useState(false);
  const [cellGroupName, setCellGroupName] = useState('');
  const [cellGroupError, setCellGroupError] = useState(null);

  const { cellGroups } = useSelector(state => state.cellGroups);
  const { sessionUser } = useSelector(state => state.session);

  const canViewCellGroups =
    (sessionUser?.rights || []).indexOf(CELL_GROUPS_VIEW) > -1;

  /* Hooks must stay above the `!open` early return below */
  useEffect(() => {
    if (open && !hideCellGroupActions && canViewCellGroups) {
      dispatch(findCellGroups());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /*
   * Loading a group only ever adds. `selectedCells` is a pre-load snapshot and every consumer's
   * reducer toggles the cell by id against live state (setupScenarioSessionCreateReducer,
   * eventDataSetCreateReducer and eventListReducer), so skipping already-selected cells keeps
   * each call a net add. A new consumer must keep toggle semantics or group loading will only
   * retain the last cell.
   */
  const handleLoadCellGroup = cellGroup => {
    setCellGroupMenuAnchor(null);
    const selectedIds = (selectedCells || []).map(c => c.id);
    (cellGroup.cells || [])
      .filter(groupCell => selectedIds.indexOf(groupCell.id) === -1)
      .forEach(groupCell =>
        onSelectCell(cells.find(c => c.id === groupCell.id) || groupCell, true)
      );
  };

  const handleCloseSaveCellGroup = () => {
    setSaveCellGroupOpen(false);
    setCellGroupName('');
    setCellGroupError(null);
  };

  const handleSaveCellGroup = () => {
    Promise.resolve(
      dispatch(saveCellGroup({ name: cellGroupName, cells: selectedCells }))
    )
      .then(response => {
        const result = response?.value?.data;
        if (!result?.success) {
          const messages = result?.validationMessages || {};
          const firstMessage = Object.keys(messages)
            .map(field => messages[field][0])
            .find(message => message);
          setCellGroupError(firstMessage || 'Unable to save cell group');
          return;
        }
        handleCloseSaveCellGroup();
        dispatch(findCellGroups());
      })
      .catch(() => setCellGroupError('Unable to save cell group'));
  };

  const findCellsByName = searchName => {
    return cells.filter(cell => {
      return (
        searchName === '' ||
        (
          cell.name.toUpperCase() +
          (cell.customerDeviceName ? cell.customerDeviceName : '')
        ).indexOf(searchName.trim().toUpperCase()) !== -1
      );
    });
  };

  const searchedCells = findCellsByName(searchName);

  const handleClose = () => {
    onClose();
  };

  const handleSearchFieldChange = value => {
    setSearchName(value);
  };

  const handleSelectFirstCell = () => {
    onSelectCell(searchedCells.length === 0 ? null : searchedCells[0]);
  };

  if (!open) {
    return null;
  }

  return (
    <Modal onClose={handleClose} open={open}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <CardHeader
          action={
            !hideCellGroupActions && (
              <>
                <Restricted right={CELL_GROUPS_VIEW}>
                  <>
                    <Button
                      disabled={cellGroups.length === 0}
                      onClick={e => setCellGroupMenuAnchor(e.currentTarget)}
                      size="small"
                      variant="outlined">
                      Load Group
                    </Button>
                    <Menu
                      anchorEl={cellGroupMenuAnchor}
                      open={cellGroupMenuAnchor !== null}
                      onClose={() => setCellGroupMenuAnchor(null)}>
                      {cellGroups.map(cellGroup => (
                        <MenuItem
                          dense
                          key={cellGroup.id}
                          onClick={() => handleLoadCellGroup(cellGroup)}>
                          {cellGroup.name} ({(cellGroup.cells || []).length})
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                </Restricted>
                <Restricted right={CELL_GROUPS_CREATE}>
                  <Button
                    className={classes.saveCellGroupButton}
                    disabled={!selectedCells || selectedCells.length === 0}
                    onClick={() => setSaveCellGroupOpen(true)}
                    size="small"
                    variant="outlined">
                    Save As Group
                  </Button>
                </Restricted>
              </>
            )
          }
          title="Select Cells"
        />
        <Divider />

        <CardContent className={classes.content}>
          <div className={classes.searchContainer}>
            <Grid container spacing={3}>
              <Grid item>
                <Search
                  autoFocus={true}
                  placeholder="Name"
                  value={searchName}
                  onSearchValueChange={handleSearchFieldChange}
                  className={classes.search}
                  hideSearchButton={true}
                  onSearch={handleSelectFirstCell}
                />
              </Grid>
              <Grid item>
                <TextField
                  style={{ marginTop: 0 }}
                  fullWidth
                  label="Status"
                  name="status"
                  margin="dense"
                  onChange={event =>
                    onChageCellStatus(
                      event.target.value ? event.target.value : null
                    )
                  }
                  select
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{ native: true }}
                  value={cellStatus ? cellStatus : ''}
                  variant="outlined">
                  <option value="" />

                  {Object.keys(CELL_STATUSES).map(status => (
                    <option key={status} value={status}>
                      {CELL_STATUSES[status]}
                    </option>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </div>
          <Divider />
          <div className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '0%' }} />
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Identifier</TableCell>
                  <TableCell>Venue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchedCells.map(cell => (
                  <TableRow key={cell.id} hover>
                    <TableCell className={classes.buttonCell}>
                      <Checkbox
                        checked={
                          selectedCells?.map(c => c.id)?.indexOf(cell.id) > -1
                        }
                        color="primary"
                        onChange={(_, value) => onSelectCell(cell, value)}
                      />
                    </TableCell>
                    <TableCell>
                      {cell.name}{' '}
                      {cell.customerDeviceName
                        ? ` / ${cell.customerDeviceName}`
                        : ''}
                    </TableCell>
                    <TableCell className={classes.labelCell}>
                      <CellStatus cell={cell} />
                    </TableCell>
                    <TableCell>{cell.sharedIdentifier}</TableCell>
                    <TableCell>{cell.currentVenue?.name}</TableCell>
                  </TableRow>
                ))}
                {!loading && searchedCells.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10}>No data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        </CardActions>

        <Modal
          disableScrollLock
          onClose={handleCloseSaveCellGroup}
          open={saveCellGroupOpen}
          style={{ zIndex: 1500 }}>
          <Card className={classes.saveCellGroupModalCard}>
            <CardHeader title="Save As Group" />
            <Divider />
            <CardContent>
              <Typography color="textSecondary" variant="body2">
                {(selectedCells || []).length} cells will be saved to this
                group.
              </Typography>
              <TextField
                autoFocus
                className={classes.saveCellGroupField}
                error={cellGroupError !== null}
                helperText={cellGroupError}
                label="Group name"
                margin="dense"
                onChange={e => setCellGroupName(e.target.value)}
                value={cellGroupName}
                variant="outlined"
              />
            </CardContent>
            <CardActions className={classes.saveCellGroupModalActions}>
              <Button onClick={handleCloseSaveCellGroup}>Cancel</Button>
              <Button
                color="primary"
                disabled={cellGroupName.trim() === ''}
                onClick={handleSaveCellGroup}
                variant="contained">
                Save
              </Button>
            </CardActions>
          </Card>
        </Modal>
      </Card>
    </Modal>
  );
};

SelectCells.displayName = 'SelectCells';

SelectCells.propTypes = {
  className: PropTypes.string,
  hideCellGroupActions: PropTypes.bool
};

export default SelectCells;
